import { splitForwardingEmail, joinForwardingEmail, splice } from './helpers'
import { formatOfficeNumber } from '../../../services/utils'

const createLocationContactInfo = ({
  active,
  country_code: countryCode,
  doctor_id: doctorId,
  forwarding_email,
  id,
  name,
  office_phone,
  office_email,
  external_outbound_number_id: outboundNumberId,
  additional_recipients,
  message_report_recipients,
}) => {
  const split = splitForwardingEmail(forwarding_email)

  return {
    active,
    doctorId,
    id,
    name,
    officePhone: formatOfficeNumber(office_phone, countryCode),
    officeEmail: office_email,
    outboundNumberId,
    additionalRecipients: additional_recipients.join(', '),
    fEmailStart: split[0],
    fEmailEnd: split[1],
    messageReportEmails: message_report_recipients.join(', '),
  }
}

export const mapContactInfo = locations =>
  locations.reduce((acc, curr) => {
    acc[curr.id] = createLocationContactInfo(curr)
    return acc
  }, {})

// circumvent eslint rule: no-prototype-builtins
const hasOwnProperty = (obj, prop) => Object.hasOwnProperty.call(obj, prop)

const formatMapping = emailMapping => ({
  forwardingEmail: joinForwardingEmail(
    emailMapping.fEmailStart,
    emailMapping.fEmailEnd,
  ),
  officeEmail: emailMapping.officeEmail || '',
})

const MULTI_MAPPED = 'not a one-to-one mapping'
const FORWARD_EMPTY = 'forwarding email empty but office email is not'

const makeErrorMessage = (errType, validLocation) => {
  const baseMsg =
    'One-to-one mapping required between reminders & office email addresses'

  if (errType === MULTI_MAPPED) {
    const forwardingEmail = joinForwardingEmail(
      validLocation.fEmailStart,
      validLocation.fEmailEnd,
    )

    return `${baseMsg}, '${forwardingEmail}' at the '${validLocation.name}' office already forwards to, '${validLocation.officeEmail}'.`
  } else if (errType === FORWARD_EMPTY) {
    return 'Reminders email cannot be blank when office email is present.'
  }

  return ''
}

const createEmailsValidation = (errType, validLocation, invalidLocation) => {
  const errorMessage = makeErrorMessage(errType, validLocation)

  return {
    isValid: !errorMessage,
    errorMessage,
    validLocationId: validLocation && validLocation.id, // only useful for testing
    invalidLocationId: invalidLocation && invalidLocation.id, // only useful for testing
  }
}

const bothEmailsEmpty = map => !map.fEmailStart && !map.officeEmail
const officeEmailButNoForwardingEmail = map =>
  !map.fEmailStart && !!map.officeEmail

const isForwardingEmailMultiMapped = (acc, forwardingEmail, officeEmail) =>
  officeEmail !== acc.forwardingEmails[forwardingEmail].officeEmail
const isOfficeEmailMultiMapped = (acc, forwardingEmail, officeEmail) =>
  forwardingEmail !== acc.officeEmails[officeEmail].forwardingEmail

const buildEmailValidationMap = (locationIds, contactInfoMaps) =>
  locationIds.reduce(
    (acc, locationId) => {
      const map = contactInfoMaps[locationId]

      const { forwardingEmail, officeEmail } = formatMapping(map)

      // check for early returns
      if (bothEmailsEmpty(map)) {
        return acc
      } else if (officeEmailButNoForwardingEmail(map)) {
        acc.errors[locationId] = createEmailsValidation(
          FORWARD_EMPTY,
          null,
          map,
        )
        acc.isValid = false
        return acc
      }

      // check forwarding email
      if (!hasOwnProperty(acc.forwardingEmails, forwardingEmail)) {
        acc.forwardingEmails[forwardingEmail] = {
          validId: locationId,
          officeEmail,
        }
      } else if (
        isForwardingEmailMultiMapped(acc, forwardingEmail, officeEmail)
      ) {
        const { validId } = acc.forwardingEmails[forwardingEmail]
        acc.errors[locationId] = createEmailsValidation(
          MULTI_MAPPED,
          contactInfoMaps[validId],
          map,
        )
        acc.isValid = false
      }

      // check office email
      if (!hasOwnProperty(acc.officeEmails, officeEmail)) {
        acc.officeEmails[officeEmail] = { validId: locationId, forwardingEmail }
      } else if (
        !!officeEmail &&
        isOfficeEmailMultiMapped(acc, forwardingEmail, officeEmail)
      ) {
        const { validId } = acc.officeEmails[officeEmail]
        acc.errors[locationId] = createEmailsValidation(
          MULTI_MAPPED,
          contactInfoMaps[validId],
          map,
        )
        acc.isValid = false
      }

      return acc
    },
    { forwardingEmails: {}, officeEmails: {}, errors: {}, isValid: true },
  )

export const validateEmailMappings = (contactInfoMaps, idToCheck = null) => {
  const locIds = Object.keys(contactInfoMaps).sort() // TODO: consider removing the sort

  // place idToCheck at the end, so it's lowest precedence in validations
  const locationIds = !idToCheck ? locIds : splice(locIds, idToCheck)

  // no need to pass on `forwardingEmail`, `officeEmail` keys
  const { isValid, errors } = buildEmailValidationMap(
    locationIds,
    contactInfoMaps,
  )

  const validStateByLocationId = locationIds.reduce(
    (map, locId) => ({
      ...map,
      [locId]: !hasOwnProperty(errors, locId),
    }),
    {},
  )

  return {
    isValid,
    errors,
    validStateByLocationId,
  }
}



// WEBPACK FOOTER //
// ./src/AddPractice/Associations/contact-info-validations/index.js